import { memo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SidebarItem } from "../../constants/leftSidebarItems";
import BackgroundImage from "../../../../../../components/ui/BackgroundImage";
import blueBg from "../../../../assets/images/blue-bg.png";
import grayBg from "../../../../assets/images/gray-bg.png";
import { useAudioEffects } from "../../../../../../hooks/use-audio-effects";
import openSound from "../../../../assets/audio/menu-open.wav";

interface NavItemProps {
  item: SidebarItem;
  highlight: boolean;
  onClick: (item: SidebarItem) => void;
}

// ─── NavItem ────────────────────────────────

const NavItem: React.FC<NavItemProps> = ({ item, highlight, onClick }) => {
  const { playEffect } = useAudioEffects();

  const Icon = item.icon;
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.button
      onClick={() => {
        playEffect(openSound);
        onClick(item);
      }}
      className={[
        "group relative w-full text-left rounded-lg",
        "flex items-center gap-3 p-5 py-7",
        // backdrop-blur-sm removed — it forced a composited layer on every nav item
        // (5+ permanent backdrop-filter layers at once is too expensive in CEF)
        "border-l",
        "transition-all duration-300 ease-out",
        "cursor-pointer bg-gradient-to-r",

        highlight
          ? "from-[#273b4f] to-[#24394f]/80 border-l-[1px] border-[#48A7FF] "
          : "from-[#313a43]/90 to-[#283746]/80 border-l-[1px] border-white/50 hover:bg-[#273b4f]/25 hover:border-[#48A7FF]",
      ].join(" ")}
      whileHover={
        shouldReduceMotion
          ? undefined
          // filter removed — scale+x is enough hover feedback and avoids a
          // compositor-context switch that caused a black flash in CEF
          : { x: 5, scale: 1.018 }
      }
      whileTap={shouldReduceMotion ? undefined : { scale: 0.97, x: 2 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      {/* Highlighted static glow overlay — was infinite opacity pulse, removed */}
      {highlight && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-lg pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, #48A7FF22, #48A7FF44, transparent)",
            opacity: 0.15,
          }}
        />
      )}

      {/* Icon */}
      <motion.div
        whileHover={
          shouldReduceMotion ? undefined : { scale: 1.18, rotate: -6 }
        }
        transition={{ type: "spring", stiffness: 380, damping: 18 }}
      >
        <BackgroundImage
          imageSrc={highlight ? blueBg : grayBg}
          imageClassName="!object-fill"
          className="w-[67px] h-[67px] flex items-center justify-center p-1"
        >
          <Icon
            size={27}
            className={[
              "transition-colors duration-300",
              highlight
                ? "text-[#48A7FF]"
                : "text-white/25 group-hover:text-[#48A7FF]",
            ].join(" ")}
          />
        </BackgroundImage>
      </motion.div>

      {/* Text */}
      <div className="flex flex-col gap-0.5 min-w-0">
        <span
          className={[
            "bebas-neue-font !font-[1000] text-sm tracking-[0.06em]",
            "transition-colors duration-300 !text-[1.65rem] mb-1 ",
            highlight
              ? "text-[#48A7FF] drop-shadow-[0_0_6px_rgba(62,198,240,0.3)]"
              : "text-[#FFFFFF]/40 group-hover:text-[#48A7FF]",
          ].join(" ")}
        >
          {item.label}
        </span>
        <span
          className={[
            "inter-font text-[0.70rem] leading-snug",
            "transition-colors duration-300",
            highlight ? "text-white/70" : "text-white/40",
          ].join(" ")}
        >
          {item.description}
        </span>
      </div>
    </motion.button>
  );
};

export default memo(NavItem);
